import { useEffect, useState, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import {
  Play, Pause, Square, Radio, History, ArrowRight, ListTree, Replace, Archive,
  PlaySquare, Zap, TriangleAlert,
} from "lucide-react";
import {
  DataTable, EmptyState, FilterSelect, IconBtn, MiniStat, PageHead, PageShell,
  Pagination, Pill, Row, SearchInput, StatStrip, Td, Toolbar,
  MODE_COLOR, relativeTime, toneOf, usePagedRows,
} from "@/components/ui/kit";

const MODE_ICON = { live: Radio, historical: History, mass_replace: Replace, archive: Archive, replay: PlaySquare };

const COLUMNS = [
  { key: "job", label: "Job" },
  { key: "mode", label: "Mode", hide: "hidden lg:table-cell" },
  { key: "account", label: "Account", hide: "hidden xl:table-cell" },
  { key: "route", label: "Source → Destination", hide: "hidden md:table-cell" },
  { key: "status", label: "Status" },
  { key: "copied", label: "Copied", align: "right" },
  { key: "queue", label: "Queue", align: "right", hide: "hidden lg:table-cell" },
  { key: "activity", label: "Last Activity", align: "right", hide: "hidden xl:table-cell" },
  { key: "actions", label: "Actions", align: "right" },
];

export default function Queue() {
  const [jobs, setJobs] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [modeFilter, setModeFilter] = useState("all");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);

  const load = useCallback(async () => {
    try {
      const [j, a] = await Promise.all([api.get("/jobs"), api.get("/accounts")]);
      setJobs(j.data);
      setAccounts(a.data);
    } catch (e) {
      console.error("queue load failed", e);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(async () => {
      try {
        const { data } = await api.get("/jobs");
        setJobs(data);
      } catch (e) {
        console.error("queue poll failed", e);
      }
    }, 5000);
    return () => clearInterval(t);
  }, [load]);

  const accName = useCallback((id) => {
    const a = accounts.find((x) => x.id === id);
    return a ? a.label || a.me?.name || a.name || a.phone || "—" : "—";
  }, [accounts]);

  const act = async (id, action) => {
    try {
      await api.post(`/jobs/${id}/${action}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || `Failed to ${action}`);
    }
  };

  const counts = useMemo(() => {
    const c = { running: 0, queued: 0, paused: 0, failed: 0, completed: 0, stopped: 0 };
    jobs.forEach((j) => {
      if (j.status === "running") c.running += 1;
      else if (["queued", "idle", "rate_limited"].includes(j.status)) c.queued += 1;
      else if (j.status === "paused") c.paused += 1;
      else if (["error", "failed"].includes(j.status)) c.failed += 1;
      else if (j.status === "completed") c.completed += 1;
      else if (j.status === "stopped") c.stopped += 1;
    });
    return c;
  }, [jobs]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const inBucket = (j) => {
      if (statusFilter === "all") return true;
      if (statusFilter === "queued") return ["queued", "idle", "rate_limited"].includes(j.status);
      if (statusFilter === "failed") return ["error", "failed"].includes(j.status);
      return j.status === statusFilter;
    };
    const order = { error: 0, failed: 0, rate_limited: 1, running: 2, queued: 3, idle: 4, paused: 5, stopped: 6, completed: 7 };
    return jobs
      .filter((j) => {
        if (!inBucket(j)) return false;
        if (modeFilter !== "all" && j.mode !== modeFilter) return false;
        if (!q) return true;
        return [j.name, j.source, ...(j.destinations || []), accName(j.source_account_id || j.account_id)]
          .filter(Boolean).some((s) => String(s).toLowerCase().includes(q));
      })
      .sort((a, b) => (order[a.status] ?? 9) - (order[b.status] ?? 9) || String(a.name).localeCompare(String(b.name)));
  }, [jobs, search, statusFilter, modeFilter, accName]);

  useEffect(() => { setPage(0); }, [search, statusFilter, modeFilter, pageSize]);
  const paged = usePagedRows(filtered, { page, pageSize });

  const pick = (key) => setStatusFilter((cur) => (cur === key ? "all" : key));

  return (
    <PageShell>
      <StatStrip cols="sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-4">
        <MiniStat label="Running" value={counts.running} icon={Zap} accent="var(--c-emerald)" tint="var(--t-emerald)"
                  testid="queue-count-running" onClick={() => pick("running")} active={statusFilter === "running"} />
        <MiniStat label="Queued" value={counts.queued} icon={ListTree} accent="var(--c-indigo)" tint="var(--t-indigo)"
                  testid="queue-count-queued" onClick={() => pick("queued")} active={statusFilter === "queued"} />
        <MiniStat label="Paused" value={counts.paused} icon={Pause} accent="var(--c-violet)" tint="var(--t-violet)"
                  testid="queue-count-paused" onClick={() => pick("paused")} active={statusFilter === "paused"} />
        <MiniStat label="Failed" value={counts.failed} icon={TriangleAlert} accent="var(--c-rose)" tint="var(--t-rose)"
                  testid="queue-count-failed" onClick={() => pick("failed")} active={statusFilter === "failed"} />
      </StatStrip>

      <PageHead
        title="Job Queue"
        count={filtered.length}
        chips={counts.completed > 0 ? <Pill tone="var(--c-sky)">{counts.completed} completed</Pill> : null}
      />

      <Toolbar>
        <SearchInput value={search} onChange={setSearch} placeholder="Search jobs, channels, accounts…" testid="queue-search" />
        <FilterSelect
          value={statusFilter} onValueChange={setStatusFilter} testid="queue-status-filter"
          options={[
            { value: "all", label: "All statuses" },
            { value: "running", label: "Running" },
            { value: "queued", label: "Queued" },
            { value: "paused", label: "Paused" },
            { value: "failed", label: "Failed" },
            { value: "stopped", label: "Stopped" },
            { value: "completed", label: "Completed" },
          ]}
        />
        <FilterSelect
          value={modeFilter} onValueChange={setModeFilter} testid="queue-mode-filter"
          options={[
            { value: "all", label: "All modes" },
            { value: "live", label: "Live" },
            { value: "historical", label: "Historical" },
            { value: "mass_replace", label: "Mass replace" },
            { value: "archive", label: "Archive" },
            { value: "replay", label: "Replay" },
          ]}
        />
      </Toolbar>

      <DataTable
        columns={COLUMNS}
        testid="queue-table"
        minWidth="min-w-[860px]"
        empty={
          paged.slice.length === 0 ? (
            <EmptyState
              icon={ListTree}
              testid="queue-empty"
              title={jobs.length === 0 ? "No jobs yet" : "No jobs match these filters"}
              hint={jobs.length === 0 ? "Create one on the Copy Jobs page to see it queued here." : undefined}
            >
              {jobs.length === 0 && (
                <Link to="/jobs" className="text-[var(--c-sky)] hover:underline text-sm font-semibold">Go to Copy Jobs →</Link>
              )}
            </EmptyState>
          ) : null
        }
      >
        {paged.slice.map((j) => {
          const tone = toneOf(j.status);
          const modeTone = MODE_COLOR[j.mode] || "var(--c-teal)";
          const Icon = MODE_ICON[j.mode] || History;
          const pct = j.total ? Math.min(100, Math.round((j.copied / j.total) * 100)) : null;
          const src = j.source_account_id || j.account_id;
          const dst = j.dest_account_id || j.account_id;
          const attention = ["error", "failed"].includes(j.status) || j.live_retry_from != null;
          return (
            <Row key={j.id} testid={`queue-job-${j.id}`} tone={attention ? "var(--c-rose)" : undefined}>
              <Td>
                <div className="flex items-center gap-2 min-w-0">
                  <Icon className="h-3.5 w-3.5 shrink-0" style={{ color: modeTone }} />
                  <div className="min-w-0">
                    <div className="font-semibold text-sm truncate max-w-[200px]" title={j.name}>{j.name}</div>
                    {j.forward_channel && (
                      <span className="text-[9px] uppercase tracking-wider font-bold text-[var(--c-violet)]"
                            title={`Forwarded via ${j.forward_channel}`} data-testid={`queue-forward-${j.id}`}>forward</span>
                    )}
                  </div>
                </div>
              </Td>
              <Td className="hidden lg:table-cell">
                <Pill tone={modeTone} size="sm">{(j.mode || "").replace("_", " ")}</Pill>
              </Td>
              <Td className="hidden xl:table-cell text-xs text-[var(--tg-muted)]">
                <div className="truncate max-w-[130px]" title={src === dst ? accName(src) : `${accName(src)} → ${accName(dst)}`}>
                  {accName(src)}{src !== dst ? ` → ${accName(dst)}` : ""}
                </div>
              </Td>
              <Td className="hidden md:table-cell">
                <div className="flex items-center gap-1.5 font-mono text-[11px] text-[var(--tg-faint)] min-w-0"
                     title={`${j.source} → ${(j.destinations || []).join(", ")}`}>
                  <span className="truncate max-w-[140px]">{j.source}</span>
                  <ArrowRight className="h-3 w-3 shrink-0" />
                  <span className="truncate max-w-[140px]">{(j.destinations || []).join(", ") || "Vault"}</span>
                </div>
              </Td>
              <Td>
                <Pill tone={tone} dot={j.status === "running"} testid={`queue-status-${j.id}`}>
                  {j.status === "rate_limited" ? "rate limit" : j.status}
                </Pill>
              </Td>
              <Td align="right">
                <div className="tabular-nums text-sm font-semibold">{(j.copied ?? 0).toLocaleString()}</div>
                {pct !== null && (
                  <div className="mt-1 w-20 ml-auto">
                    <Progress value={pct} className="h-1 bg-[var(--tg-elevated)]" />
                  </div>
                )}
              </Td>
              <Td align="right" className="hidden lg:table-cell tabular-nums text-xs text-[var(--tg-faint)]">
                {j.total ? Math.max(0, j.total - (j.copied ?? 0)).toLocaleString() : "—"}
              </Td>
              <Td align="right" className="hidden xl:table-cell text-[11px] text-[var(--tg-faint)] whitespace-nowrap">
                {relativeTime(j.last_copied_at) || "—"}
              </Td>
              <Td align="right">
                <div className="flex items-center justify-end gap-1">
                  {j.status === "running" && (
                    <IconBtn icon={Pause} title="Pause job" tone="var(--c-violet)" onClick={() => act(j.id, "pause")} testid={`queue-pause-${j.id}`} />
                  )}
                  {j.status === "paused" && (
                    <IconBtn icon={Play} title="Resume job" tone="var(--c-violet)" onClick={() => act(j.id, "resume")} testid={`queue-resume-${j.id}`} />
                  )}
                  {["idle", "queued", "completed", "stopped", "error", "failed"].includes(j.status) && (
                    <IconBtn icon={Play} title="Start job" tone="var(--c-emerald)" onClick={() => act(j.id, "start")} testid={`queue-start-${j.id}`} />
                  )}
                  {["running", "paused", "queued", "rate_limited"].includes(j.status) && (
                    <IconBtn icon={Square} title="Stop job" tone="var(--c-amber)" onClick={() => act(j.id, "stop")} testid={`queue-stop-${j.id}`} />
                  )}
                </div>
              </Td>
            </Row>
          );
        })}
      </DataTable>

      {filtered.length > pageSize && (
        <Pagination
          page={paged.safePage} pageCount={paged.pageCount} total={paged.total}
          from={paged.from} to={paged.to} pageSize={pageSize} onPageSize={setPageSize}
          onPrev={() => setPage((p) => Math.max(0, p - 1))}
          onNext={() => setPage((p) => Math.min(paged.pageCount - 1, p + 1))}
          testid="queue-pagination"
        />
      )}
    </PageShell>
  );
}
